import { format } from "date-fns"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import {
  LICENSE_STATUS_ACTIVE,
  LICENSE_STATUS_NOT_RENEWING,
  LICENSE_STATUS_TRIAL,
} from "@/v2-backoffice-client/license/license.constants"
import type { UsageMcpPortfolioQuery } from "@/v2-backoffice-client/usage/mcp/__types__/usage.mcpPortfolio.query"
import type { UsageMcpCustomerQuery } from "@/v2-backoffice-client/usage/mcp/__types__/usage.mcpCustomer.query"
import {
  formatMcpClient,
  formatMcpTool,
} from "@/v2-backoffice-client/usage/mcp/usage.mcp.constants"
import {
  formatIsoWeek,
  mcpTrendPct,
  type UsageMcpCountRow,
  type UsageMcpToolGroupShare,
} from "@/v2-backoffice-client/usage/mcp/usage.mcp.utils"
import { describeTrend, list } from "@/v2-backoffice-client/usage/usage.text"

type Portfolio = UsageMcpPortfolioQuery["getUsageMcpForPortfolio"]
type PortfolioSummary = NonNullable<Portfolio["Summary"]>
type PortfolioCustomer = Portfolio["Customers"][number]
type Week = Portfolio["Weeks"][number]

type CustomerScope = UsageMcpCustomerQuery["getUsageMcpByCustomer"]
type CustomerSummary = NonNullable<CustomerScope["Summary"]>

/** Share of a whole, in whole percent. */
function pct(part: number, whole: number) {
  return whole > 0 ? Math.round((part * 100) / whole) : 0
}

function plural(count: number, noun: string, nouns = `${noun}s`) {
  return `${formatNumber(count)} ${count === 1 ? noun : nouns}`
}

/** "A, B and C" */

function isLicensed(customer: PortfolioCustomer) {
  return (
    customer.LicenseStatusCode === LICENSE_STATUS_TRIAL ||
    customer.LicenseStatusCode === LICENSE_STATUS_ACTIVE ||
    customer.LicenseStatusCode === LICENSE_STATUS_NOT_RENEWING
  )
}

/**
 * The complete weeks of a series (today's week is partial and never a
 * trend base): first, the one before last, and last. Null under three.
 */
function completeWeeks(weeks: Week[], today = new Date()) {
  const thisMonday = new Date(today)
  thisMonday.setUTCHours(0, 0, 0, 0)
  thisMonday.setUTCDate(
    thisMonday.getUTCDate() - ((thisMonday.getUTCDay() + 6) % 7),
  )
  const complete = weeks.filter(
    (week) => new Date(week.WeekStart).getTime() < thisMonday.getTime(),
  )
  if (complete.length < 2) return null
  return {
    first: complete[0],
    previous: complete[complete.length - 2],
    last: complete[complete.length - 1],
  }
}

export type McpPortfolioStory = {
  /** The opening paragraph: how much, since when, how fast it grows. */
  summary: string[]
  /** What the use looks like: areas, the top tools, a working day. */
  usage: string[]
  /** The AI clients, by customers and people. */
  clients: string[]
  /** The license gate: who knocked, where they stand. */
  gate: string[]
  /** The Dataset MCP in one paragraph. */
  dataset: string[]
}

/**
 * The portfolio report's prose, derived from the same numbers the cards
 * show so the two never disagree. Paragraphs, not bullets: this is what a
 * sales or CS colleague reads first and lifts into an e-mail.
 */
export function deriveMcpPortfolioStory(input: {
  summary: PortfolioSummary
  weeks: Week[]
  customers: PortfolioCustomer[]
  tools: UsageMcpCountRow[]
  clients: UsageMcpCountRow[]
  groups: UsageMcpToolGroupShare[]
  datasetSummary?: PortfolioSummary | null
  datasetCustomers?: PortfolioCustomer[]
}): McpPortfolioStory {
  const {
    summary,
    weeks,
    customers,
    tools,
    clients,
    groups,
    datasetSummary,
    datasetCustomers = [],
  } = input
  const story: McpPortfolioStory = {
    summary: [],
    usage: [],
    clients: [],
    gate: [],
    dataset: [],
  }

  // --- Summary ------------------------------------------------------------
  const firstDay = summary.FirstDay ? new Date(summary.FirstDay) : null
  const span = completeWeeks(weeks)
  {
    const sentences: string[] = []
    sentences.push(
      `${plural(summary.CustomersTotal ?? 0, "customer")} and ${plural(summary.UsersTotal, "person", "people")} have used the Placepoint MCP${
        firstDay ? ` since ${format(firstDay, "d MMMM")}` : ""
      }, ${formatNumber(summary.CallsTotal)} tool calls in all.`,
    )
    if (span) {
      const { first, previous, last } = span
      const step = mcpTrendPct(last.Calls, previous.Calls)
      const whole =
        first.WeekStart !== last.WeekStart
          ? mcpTrendPct(last.Calls, first.Calls)
          : step
      const verb =
        whole == null
          ? "has started"
          : whole >= 25
            ? "is growing"
            : whole <= -20
              ? "is falling"
              : "is steady"
      sentences.push(
        `Use ${verb}: ${formatNumber(first.Calls)} calls from ${plural(first.Users, "person", "people")} in ${formatIsoWeek(first.WeekStart)}, ${formatNumber(last.Calls)} calls from ${plural(last.Users, "person", "people")} in ${formatIsoWeek(last.WeekStart)}${
          step != null ? ` (${describeTrend(step)} on the week before)` : ""
        }.`,
      )
    }
    if ((summary.NewCustomers28d ?? 0) > 0) {
      sentences.push(
        `${plural(summary.NewCustomers28d ?? 0, "customer")} made ${summary.NewCustomers28d === 1 ? "its" : "their"} first call in the last four weeks.`,
      )
    }
    story.summary.push(sentences.join(" "))
  }

  // --- Usage --------------------------------------------------------------
  if (groups.length > 0 && summary.Calls28d > 0) {
    const [top, second] = groups
    const sentences = [
      `${top.label} is the biggest use: ${pct(top.calls, summary.Calls28d)} % of the calls${
        second
          ? `, followed by ${second.label} at ${pct(second.calls, summary.Calls28d)} %`
          : ""
      }.`,
    ]
    const topTools = tools.filter((tool) => tool.Calls28d > 0).slice(0, 3)
    if (topTools.length > 0) {
      sentences.push(
        `The single most used tool is ${formatMcpTool(topTools[0].Key)} (${formatNumber(topTools[0].Calls28d)} calls by ${plural(topTools[0].Users28d, "person", "people")})${
          topTools.length > 1
            ? `, ahead of ${list(topTools.slice(1).map((tool) => `${formatMcpTool(tool.Key)} (${formatNumber(tool.Calls28d)})`))}`
            : ""
        }.`,
      )
    }
    if (summary.MedianCallsPerPersonDay28d != null) {
      sentences.push(
        `A typical working day on the MCP holds ${formatNumber(Math.round(summary.MedianCallsPerPersonDay28d))} calls${
          summary.MedianToolsPerPersonDay28d != null
            ? ` across ${formatNumber(Math.round(summary.MedianToolsPerPersonDay28d))} different tools`
            : ""
        }.`,
      )
    }
    if (summary.Calls28d > 0 && summary.Errors28d > 0) {
      sentences.push(
        `${pct(summary.Errors28d, summary.Calls28d)} % of the calls failed — most often an AI client sending an argument the tool does not understand.`,
      )
    }
    story.usage.push(sentences.join(" "))
  }

  // --- Clients ------------------------------------------------------------
  {
    const byPeople = clients
      .filter((client) => client.Users28d > 0 && client.Key !== "")
      .toSorted((a, b) => b.Users28d - a.Users28d)
    if (byPeople.length > 0 && summary.Users28d > 0) {
      const customersUsing = (code: string) =>
        customers.filter(
          (customer) =>
            customer.Calls28d > 0 && customer.Clients.includes(code),
        )
      const [lead, ...rest] = byPeople
      const sentences = [
        `${formatMcpClient(lead.Key)} is the client almost everyone uses: ${lead.Users28d} of the ${summary.Users28d} people active in the last 28 days, at ${plural(customersUsing(lead.Key).length, "customer")}.`,
      ]
      for (const client of rest) {
        const using = customersUsing(client.Key)
        const name = formatMcpClient(client.Key)
        if (using.length === 0) continue
        if (using.length <= 4) {
          sentences.push(
            `${name} is the choice at ${list(using.map((customer) => customer.Name))} (${plural(client.Users28d, "person", "people")}).`,
          )
        } else {
          sentences.push(
            `${plural(client.Users28d, "person", "people")} at ${plural(using.length, "customer")} connect through ${name}.`,
          )
        }
      }
      sentences.push("A person can use more than one client.")
      story.clients.push(sentences.join(" "))
    }
  }

  // --- License gate -------------------------------------------------------
  if (summary.Denied28d > 0) {
    const refused = customers.filter((customer) => customer.Denied28d > 0)
    const backIn = refused.filter(isLicensed)
    const outside = refused.filter((customer) => !isLicensed(customer))
    const sentences = [
      `In the last 28 days ${plural(summary.DeniedUsers28d, "person", "people")} at ${plural(summary.DeniedCustomers28d ?? refused.length, "customer")} tried to use the MCP without a seat and were turned away, ${formatNumber(summary.Denied28d)} attempts in all.`,
    ]
    if (backIn.length > 0) {
      sentences.push(
        `${backIn.length === 1 ? `${backIn[0].Name} holds` : `${backIn.length} of those customers now hold`} an MCP license, so the door is open again there.`,
      )
    }
    if (outside.length > 0) {
      sentences.push(
        `${list(outside.slice(0, 6).map((customer) => customer.Name))}${outside.length > 6 ? ` and ${outside.length - 6} more` : ""} ${outside.length === 1 ? "is" : "are"} still outside — each is a seat to sell.`,
      )
    }
    story.gate.push(sentences.join(" "))
  }

  // --- Dataset MCP --------------------------------------------------------
  if (datasetSummary && datasetSummary.CallsTotal > 0) {
    const internal = datasetSummary.InternalCallsTotal ?? 0
    const sentences = [
      `The Dataset MCP lets an AI client create and edit map datasets in a customer's project.`,
      internal > datasetSummary.CallsTotal
        ? `So far it is mostly our own tool: ${formatNumber(internal)} internal calls against ${formatNumber(datasetSummary.CallsTotal)} from ${plural(datasetSummary.UsersTotal, "person", "people")} at ${plural(datasetSummary.CustomersTotal ?? 0, "customer")}.`
        : `${formatNumber(datasetSummary.CallsTotal)} calls from ${plural(datasetSummary.UsersTotal, "person", "people")} at ${plural(datasetSummary.CustomersTotal ?? 0, "customer")}, ${formatNumber(internal)} internal.`,
    ]
    const active = datasetCustomers
      .filter((customer) => customer.Calls28d > 0)
      .toSorted((a, b) => b.Calls28d - a.Calls28d)
    if (active.length > 0) {
      sentences.push(
        `In the last 28 days: ${list(active.slice(0, 4).map((customer) => `${customer.Name} (${formatNumber(customer.Calls28d)})`))}.`,
      )
    }
    story.dataset.push(sentences.join(" "))
  }

  return story
}

/**
 * One customer's story in a paragraph or two: how much and by whom, what
 * for, from which clients, and whether the license gate is in the way.
 */
export function deriveMcpCustomerStory(input: {
  summary: CustomerSummary
  weeks: CustomerScope["Weeks"]
  members: CustomerScope["Members"]
  tools: UsageMcpCountRow[]
  clients: UsageMcpCountRow[]
  groups: UsageMcpToolGroupShare[]
  productName: string
}): string[] {
  const { summary, weeks, members, tools, clients, groups, productName } = input
  const paragraphs: string[] = []

  if (summary.Calls28d > 0 || summary.CallsTotal > 0) {
    const trend = mcpTrendPct(summary.Calls28d, summary.CallsPrev28d ?? 0)
    const firstDay = summary.FirstDay ? new Date(summary.FirstDay) : null
    const sentences: string[] = []
    sentences.push(
      `${plural(summary.UsersTotal, "person", "people")} here ${summary.UsersTotal === 1 ? "has" : "have"} used the ${productName}${
        firstDay ? ` since ${format(firstDay, "d MMMM")}` : ""
      }: ${formatNumber(summary.CallsTotal)} tool calls in all, ${formatNumber(summary.Calls28d)} in the last 28 days${
        trend != null
          ? `, ${describeTrend(trend)} on the four weeks before`
          : summary.CallsPrev28d === 0 && summary.Calls28d > 0
            ? " — their first month on it"
            : ""
      }.`,
    )
    const span = completeWeeks(weeks as Week[])
    if (span && span.last.Calls > 0) {
      sentences.push(
        `Last week (${formatIsoWeek(span.last.WeekStart)}) they made ${formatNumber(span.last.Calls)} calls.`,
      )
    }
    const topMember = members.find((member) => member.Calls28d > 0)
    if (topMember && summary.Calls28d > 0 && summary.Users28d > 1) {
      const name =
        `${topMember.FirstName ?? ""} ${topMember.LastName ?? ""}`.trim() ||
        topMember.Email ||
        `User ${topMember.UserId}`
      sentences.push(
        `${name} does ${pct(topMember.Calls28d, summary.Calls28d)} % of it.`,
      )
    }
    paragraphs.push(sentences.join(" "))
  }

  {
    const sentences: string[] = []
    const [top, second] = groups
    if (top && summary.Calls28d > 0) {
      sentences.push(
        `Most of the work is ${top.label} (${pct(top.calls, summary.Calls28d)} % of the calls)${
          second
            ? `, then ${second.label} (${pct(second.calls, summary.Calls28d)} %)`
            : ""
        }.`,
      )
    }
    const topTools = tools.filter((tool) => tool.Calls28d > 0).slice(0, 3)
    if (topTools.length > 0) {
      sentences.push(
        `The tools they reach for most are ${list(topTools.map((tool) => formatMcpTool(tool.Key)))}.`,
      )
    }
    const byPeople = clients
      .filter((client) => client.Users28d > 0 && client.Key !== "")
      .toSorted((a, b) => b.Users28d - a.Users28d)
    if (byPeople.length > 0) {
      sentences.push(
        `They connect from ${list(byPeople.slice(0, 3).map((client) => formatMcpClient(client.Key)))}.`,
      )
    }
    if (sentences.length > 0) paragraphs.push(sentences.join(" "))
  }

  if (summary.Denied28d > 0) {
    paragraphs.push(
      `${plural(summary.DeniedUsers28d, "person", "people")} ${summary.DeniedUsers28d === 1 ? "has" : "have"} tried to use the ${productName} without a seat and been turned away, ${formatNumber(summary.Denied28d)} times in the last 28 days${
        summary.LastDeniedDay
          ? `, most recently on ${format(new Date(summary.LastDeniedDay), "d MMMM")}`
          : ""
      }. That is a seat to sell.`,
    )
  }

  return paragraphs
}
